import React, { JSX, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import Actions from "../Actions";
import Button from "../Button";
import FontAwesomeIcon from "../Icon";
import Table, { Hidden } from "../Table";
import Label from "../Label";
import Overlay from "../Overlay";
import moment from "moment";
import Empty from "../Empty";
import CommonAreaAssetForm from "../../components/CommonAreaAssetForm";
import {
  clearCommonAreaAssetFormData,
  setCommonAreaAssetFormData,
} from 'src/store/actions/commonAreaAssetForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { variables } from 'src/Tenants';
import Search from "src/components/Search/Search";
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';

interface CommonAreaAssetsTableProps {
  commonArea: any;
  onSearch: (e: any) => void;
  commonAreaAssets: any[];
  onSaveCommonAreaAsset: (...args: any[]) => void;
  onDeleteCommonAreaAsset: (id: string | number) => void;
}

const CommonAreaAssetsTable = ({
  commonArea,
  onSearch,
  commonAreaAssets,
  onSaveCommonAreaAsset,
  onDeleteCommonAreaAsset,
}: CommonAreaAssetsTableProps): JSX.Element => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);

  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [newAsset, setNewAsset] = useState<boolean>(false);

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const homeOwnerCheck =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === "Home Owner" ||
      user.roles[0].role.name === "Occupant");

  return (
    <>
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Common Areas..."
          onChange={onSearch}
          theme={undefined}
        />
        {homeOwnerCheck ? null : (
          <Button
            color="teal"
            label="Add Common Area Asset"
            onClick={() => {
              dispatch(
                setCommonAreaAssetFormData({
                  assetWarrantyDefaultStartDate: null,
                  dateOfPurchase:
                    commonArea && commonArea.dateOfPurchase
                      ? commonArea.dateOfPurchase
                      : null,
                })
              );
              setShowOverlay(true);
              setNewAsset(true);
            }}
            icon={<FontAwesomeIcon glyph="home" weight="light" size="small" />}
            size="small"
          />
        )}
      </Actions>
      {commonAreaAssets.length > 0 ? (
        <>
          <Table
            columns={[
              {
                label: 'Item',
                sortable: true,
                accessor: 'item.name',
                cell: (data: any) => <Link>{data.item.name}</Link>,
              },
              {
                label: 'Make',
                cell: (data: any) =>
                  data.item && data.item.makeName ? data.item.makeName : '—',
              },
              {
                label: 'Model',
                cell: (data: any) =>
                  data.item && data.item.model ? data.item.model : '—',
              },
              {
                xsHide: true,
                label: 'Warranty Expiration',
                cell: (data: any) => {
                  const color =
                    data.warrantDays < 30
                      ? 'red'
                      : data.warrantDays < 60
                        ? 'yellow'
                        : 'teal';
                  return (
                    <Label
                      color={color}
                      label={
                        data.item.warrantyYears === '0'
                          ? '----- NA -----'
                          : moment(data.warrantExpiration).format('MM/DD/YYYY')
                      }
                    />
                  );
                },
              },
              {
                cell: (data: any) =>
                  homeOwnerCheck ? null : (
                    <Hidden>
                      <Button
                        color="semantic"
                        label={
                          <FontAwesomeIcon glyph="pencil" weight="light" />
                        }
                        onClick={() => {
                          dispatch(
                            setCommonAreaAssetFormData({
                              ...data,
                              dateOfPurchase: data.dateOfPurchase,
                            })
                          );
                          setShowOverlay(true);
                          setNewAsset(false);
                        }}
                      />
                      <Button
                        color="semantic"
                        onClick={(e: any) =>
                          window.confirm(
                            `Are you sure you want to delete ${data.item.name}?`
                          ) && onDeleteCommonAreaAsset(data.id)
                        }
                        label={<FontAwesomeIcon glyph="trash" weight="light" />}
                      />
                    </Hidden>
                  ),
              },
            ]}
            data={commonAreaAssets}
            paginate={{ itemsPerPage, position: 'both' }}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </>
      ) : (
        <Empty
          headline="This common area is empty"
          strapline="Add assets to this common area and attach relevant documentation such as manuals and warranties."
          searchIsActive={search && search.commonArea ? search.commonArea : ''}
          cta={
            homeOwnerCheck ? null : (
              <Button
                color="teal"
                label="Add Common Area Asset"
                onClick={() => {
                  dispatch(
                    setCommonAreaAssetFormData({
                      assetWarrantyDefaultStartDate: null,
                      dateOfPurchase:
                        commonArea && commonArea.dateOfPurchase
                          ? commonArea.dateOfPurchase
                          : null,
                    })
                  );
                  setShowOverlay(true);
                  setNewAsset(true);
                }}
                icon={
                  <FontAwesomeIcon glyph="boxes" weight="light" size="small" />
                }
                size="small"
              />
            )
          }
          image={
            <img
              src="/assets/empty/assets.svg"
              alt={`This ${variables.suite} is empty`}
            />
          }
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <CommonAreaAssetForm
            newAsset={newAsset}
            commonAreaId={commonArea.id}
            onClose={() => {
              setShowOverlay(false);
              dispatch(clearCommonAreaAssetFormData());
            }}
            onSaveCommonAreaAsset={onSaveCommonAreaAsset}
          />
        }
      />
    </>
  );
};

export default CommonAreaAssetsTable;
